import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const skipStorageKey = (userId) => `entrea_mobile_pin_suggest_skipped_${String(userId || '').trim()}`;

/**
 * Après une première connexion mobile sans PIN : propose d’en définir un pour les prochaines ouvertures (déverrouillage rapide).
 */
const MobileSuggestPin = () => {
  const navigate = useNavigate();
  const { loading, token, user, hasMobilePinForCurrentUser, setMobilePin, logout } = useAuth();
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const alreadyHasPin = Boolean(user?.id && hasMobilePinForCurrentUser());

  useEffect(() => {
    if (loading) return;
    if (!token) {
      navigate('/mobile/login', { replace: true });
      return;
    }
    if (user?.must_change_password) {
      navigate('/mobile/first-login-password', { replace: true });
      return;
    }
    if (alreadyHasPin) {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, token, user?.must_change_password, navigate, alreadyHasPin]);

  const skip = () => {
    const uid = String(user?.id || user?.sub || '').trim();
    if (uid) {
      try {
        localStorage.setItem(skipStorageKey(uid), '1');
      } catch {
        /* ignore */
      }
    }
    navigate('/dashboard', { replace: true });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (String(pin || '').trim().length < 4) {
      setError('Le PIN doit contenir au moins 4 chiffres.');
      return;
    }
    if (pin !== pinConfirm) {
      setError('Les deux saisies du PIN ne correspondent pas.');
      return;
    }
    setSubmitting(true);
    const ok = setMobilePin(pin, user?.id || user?.sub);
    setSubmitting(false);
    if (!ok) {
      setError('Impossible d’enregistrer le PIN sur cet appareil.');
      return;
    }
    navigate('/dashboard', { replace: true });
  };

  if (loading || !token || !user) {
    return (
      <div className="min-h-screen bg-[#f4f6fb] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f6fb] flex flex-col px-5 pt-[max(24px,env(safe-area-inset-top))] pb-[calc(env(safe-area-inset-bottom)+24px)]">
      <div className="mx-auto w-full max-w-sm flex-1 flex flex-col justify-center">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_10px_28px_rgba(15,51,80,0.10)]">
          <h1 className="text-xl font-extrabold text-slate-900 text-center">Sécuriser cet appareil</h1>
          <p className="mt-2 text-sm text-slate-600 text-center leading-relaxed">
            Définissez un code PIN sur cet appareil : aux prochaines ouvertures, vous pourrez déverrouiller Entrea avec ce PIN
            après avoir saisi votre mot de passe (session fermée) ou directement si votre session est encore active.
          </p>

          {error ? (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          ) : null}

          <form onSubmit={onSubmit} className="mt-5 space-y-4">
            <div>
              <label htmlFor="suggest-pin" className="block text-sm font-medium text-slate-700 mb-1.5">
                PIN (4 chiffres minimum)
              </label>
              <input
                id="suggest-pin"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                value={pin}
                onChange={(e) => setPin(String(e.target.value || '').replace(/\D/g, ''))}
                className="w-full min-h-[52px] rounded-xl border border-slate-300 px-4 text-center text-lg tracking-[0.18em] focus:outline-none focus:ring-2 focus:ring-entrea-royal/30"
                autoComplete="new-password"
                autoFocus
              />
            </div>
            <div>
              <label htmlFor="suggest-pin-2" className="block text-sm font-medium text-slate-700 mb-1.5">
                Confirmer le PIN
              </label>
              <input
                id="suggest-pin-2"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                value={pinConfirm}
                onChange={(e) => setPinConfirm(String(e.target.value || '').replace(/\D/g, ''))}
                className="w-full min-h-[52px] rounded-xl border border-slate-300 px-4 text-center text-lg tracking-[0.18em] focus:outline-none focus:ring-2 focus:ring-entrea-royal/30"
                autoComplete="new-password"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full min-h-[52px] rounded-xl bg-entrea-royal text-white font-semibold text-base shadow-sm active:scale-[0.99] disabled:opacity-60"
            >
              {submitting ? 'Enregistrement…' : 'Activer le PIN'}
            </button>
          </form>

          <button
            type="button"
            onClick={skip}
            className="mt-4 w-full min-h-[44px] rounded-xl border border-slate-200 bg-slate-50 text-sm font-semibold text-slate-700"
          >
            Plus tard
          </button>

          <button type="button" onClick={() => logout()} className="mt-3 w-full text-sm text-slate-500 hover:text-slate-700">
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileSuggestPin;
